/**
 * Authors: mariushirjanu, koenweyn
 * Created: 27/05/2021
 */
import * as _ from 'underscore';
import { util } from '../../../shared/util';
import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BodyClassService } from '../../../shared/services/body-class.service';
import { ICodeValue, IFaq } from '../../../../type_definitions/datatypes';
import { TranslateService } from '@ngx-translate/core';

import { FaqDataService } from '../../../shared/faq/faq-data.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { TitleService } from '../../../shared/services/title.service';
import { GaMessageService } from '../../../shared/message/ga-message.service';
import { ScrollService } from '../../../shared/scroll/scroll.service';
import { StateService } from '@uirouter/angular';
import { SendMailService } from '../../../shared/send-mail/send-mail.service';
import { IMailRequest } from '../../../shared/send-mail/send-mail-data.service';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { SEC_SEND_FAQ } from '../../../shared/services/auth/security-objects';

export interface IFaqItem extends IFaq {
  collapsed:boolean;
  scrollTarget:string;
  userList?: string;
}

@Component({
  selector: 'faq-list',
  template:
    `
      <h2 *ngIf="loading">
        <activity-indicator toLoad="GA.GENERAL.FAQ"></activity-indicator>
      </h2>

      <h2 *ngIf="!loading">{{ title || keyword }}
        <button class="btn float-end" (click)="sendMail()" *ngIf="faqs?.length && hasPermissionToSendFAQ">
          <i class="fa fa-envelope-o"></i>&nbsp;<translate-label key="GA.BUTTONS.SEND_LABEL" label="GA.GENERAL.FAQ"></translate-label>
        </button>
      </h2>

      <div class="search-form" *ngIf="!loading">
        <form novalidate>
          <formly [form]="form" [fields]="findFields" [model]="findModel">
            <button class="btn btn-primary" *ngIf="!keyword" [busyButton]="searching" (click)="search()" translate>GA.BUTTONS.SEARCH</button>
            <div class="highlight-controls" *ngIf="faqs?.length">
              <button class="btn clear-button" [disabled]="!findModel.text" (click)="find()"><i class="fa fa-search"></i> <span translate>GA.GENERAL.HIGHLIGHT</span></button>
              <button class="btn clear-button" (click)="clear()"><span translate>GA.GENERAL.CLEAR</span></button>
            </div>
          </formly>
        </form>
      </div>

      <h2 *ngIf="searching">
        <activity-indicator text="GA.GENERAL.SEARCHING"></activity-indicator>
      </h2>

      <h3 *ngIf="noResultsFound" translate>GA.GENERAL.NO_RESULTS_FOUND</h3>

      <div *ngIf="!loading && faqs?.length" class="faq-list">
        <div class="expand-controls">
          <button class="btn expand-all-button" (click)="expandAll()"><i class="fa fa-expand"></i> <span translate>GA.GENERAL.EXPAND_ALL</span></button>
          <button class="btn collapse-all-button" (click)="collapseAll()"><i class="fa fa-compress"></i> <span translate>GA.GENERAL.COLLAPSE_ALL</span></button>
        </div>

        <div *ngIf="keywords.length">
          <div *ngFor="let keyword of keywords; trackById">
            <h2 class="keyword-title">{{keyword}}</h2>
            <collapsible-pane title="{{faq.question}}" [collapsed]="faq.collapsed" (onChange)="toggleFaq(faq, $event)" *ngFor="let faq of groupedFaqs[keyword]; trackById">
              <faq-list-content [faq]="faq"></faq-list-content>
            </collapsible-pane>
          </div>
        </div>
        <div *ngIf="!keywords.length">
          <collapsible-pane title="{{faq.question}}" [collapsed]="faq.collapsed" (onChange)="toggleFaq(faq, $event)" *ngFor="let faq of faqs; trackById">
            <faq-list-content [faq]="faq"></faq-list-content>
          </collapsible-pane>
        </div>
      </div>
    `,
  styleUrls: [ './faq.scss' ],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: true
})
export class FaqListComponent implements OnInit {
  @Input() public keyword:string;
  @Input() public webshop:number;
  @Input() public title:string;
  @Input() public query:string;
  @Input() public autoload:string;

  public loading:boolean;
  public searching:boolean;
  public noResultsFound:boolean;
  public faqs:IFaqItem[];
  public keywords:string[];
  public groupedFaqs:{
    [ index:string ]:IFaqItem[]
  };

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public findFields:IExtraFieldConfigurationObject[];
  public findModel:{
    text?:string
  } = {};
  public hasPermissionToSendFAQ:boolean;

  constructor(
    private faqDataService:FaqDataService,
    private sendMailService:SendMailService,
    private formlyService:GaFormlyService,
    private gaMessageService:GaMessageService,
    private scrollService:ScrollService,
    private titleService:TitleService,
    private translateService:TranslateService,
    private stateService:StateService,
    private authService:AuthService,
    bodyClassService:BodyClassService
  ) {
    bodyClassService.add('faq-container');
  }

  public ngOnInit():void {
    this.createFields();

    this.query = _.isNull(this.query) ? undefined : this.query;
    this.findModel.text = this.query;

    if (this.keyword || this.query || this.autoload) {
      this.loadFaqList();
    }
    if (!this.title && !this.keyword) {
      this.title = this.translateService.instant('GA.GENERAL.FAQ');
    }
    this.titleService.setTitle(this.title || this.keyword);
  }

  public toggleFaq(faqItem:IFaqItem, collapsed:boolean):void {
    faqItem.collapsed = collapsed;
  }

  public sendMail():void {
    this.sendMailService.showSendMailDialog(
      this.translateService.instant('GA.BUTTONS.SEND_LABEL', { label: this.translateService.instant('GA.GENERAL.FAQ') }),
      this.title || this.keyword,
      undefined,
      this.onMailSend
    );
  }

  public expandAll():void {
    this.faqs
      .filter((faq:IFaqItem) => faq.collapsed)
      .forEach((faq:IFaqItem) => this.toggleFaq(faq, false));
  }

  public collapseAll():void {
    this.faqs
      .filter((faq:IFaqItem) => !faq.collapsed)
      .forEach((faq:IFaqItem) => this.toggleFaq(faq, true));
  }

  public clear():void {
    this.clearFAQs();
  }

  public search():void {
    if (!this.findModel.text || this.findModel.text === this.query) {
      this.query = this.findModel.text;
      this.loadFaqList(true);
    } else {
      this.stateService.go(this.stateService.current, {
        query: this.findModel.text,
        autoload: undefined
      }, { inherit: true });
    }
  }

  private clearFAQs() {
    const clearRE:RegExp = /(<span class="highlight">)|(<\/span>)/gi;
    this.faqs
      .forEach((faq:IFaqItem) => {
        faq.question = faq.question?.replace(clearRE, '');
        faq.answer = faq.answer?.replace(clearRE, '');
      });
  }

  public find():void {
    this.clearFAQs();
    if (!this.findModel.text) {
      return;
    }

    // search and highlight text but excluding HTML tags attribute value
    const searchText:RegExp = new RegExp(`(${util.regexEscape(this.findModel.text)})(?![^<>]*>)`, 'gi');
    let firstFaqMatchTarget:string;
    this.faqs
      .forEach((faq:IFaqItem) => {
        const answer:string = faq.answer?.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // replace diacritics
        if (answer?.search(searchText) >= 0 || faq.question?.search(searchText) >= 0) {
          if (!firstFaqMatchTarget) {
            firstFaqMatchTarget = faq.scrollTarget;
          }
          faq.question = faq.question?.replace(searchText, '<span class="highlight">$1</span>');
          faq.answer = faq.answer?.replace(searchText, `<span class="highlight">$1</span>`);
          this.toggleFaq(faq, false);
        }
      });

    if (firstFaqMatchTarget) {
      this.scrollService.scrollTo(firstFaqMatchTarget);
    }
  }

  private onMailSend = (model:IMailRequest):Promise<any> => {
    return this.faqDataService.sendFaqs(model, this.keyword, this.webshop, this.query)
      .then(() => {
        this.gaMessageService.showFeedbackMessage(this.translateService.instant('GA.GENERAL.SENT', { label: this.translateService.instant('GA.GENERAL.FAQ') }));
      });
  };

  private createFields():void {
    this.findFields = [
      this.formlyService.anInput('text', 'GA.GENERAL.SEARCH_TEXT')
        .withoutWrapper()
        .withPlaceholder('GA.GENERAL.SEARCH_TEXT')
        .withExtraOptions({ templateOptions: { inputFocus: true } })
        .withInputType('search')
        .build()
    ];
  }

  private setBusy(force:boolean, busy:boolean):void {
    if (force || this.query) {
      this.searching = busy;
    } else {
      this.loading = busy;
    }
  }

  private loadFaqList(force?:boolean):void {
    this.noResultsFound = false;
    this.faqs = [];
    if (force || this.keyword || this.query || this.autoload === 'true') {
      this.setBusy(force, true);
      this.faqDataService.listFaqs(this.keyword, this.webshop, this.query)
        .then((faqs:IFaq[]) => {
          this.noResultsFound = !faqs.length;
          this.faqs = [];
          this.keywords = [];
          this.groupedFaqs = {};
          // simple list
          if (this.keyword || this.query) {
            this.faqs = _.map(faqs, (faq:IFaq) => {
              const faqItem:IFaqItem = <IFaqItem>faq;
              faqItem.collapsed = true;
              faqItem.scrollTarget = 'faq-' + faqItem.id;
              return faqItem;
            });
            // if one of the faqs has permission to send faq we still show the send faq button
            if (this.faqs?.length) {
              _.each(this.faqs, (faqItem:IFaqItem) => {
                this.authService.selectPermissions(faqItem.securedDomain);
                if(this.authService.viewWrite(SEC_SEND_FAQ)){
                  this.hasPermissionToSendFAQ = true;
                }
              });
            }
          } else { //group the FAQ items by keyword
            faqs = _.sortBy(faqs, faqItem => faqItem.keywords && faqItem.keywords.length && faqItem.keywords[ 0 ].value);
            _.each(faqs, (faqItem:IFaqItem) => {
              faqItem.collapsed = true;
              _.each(faqItem.keywords, (keyword:ICodeValue, index) => {
                if (!_.contains(this.keywords, keyword.value)) {
                  this.keywords.push(keyword.value);
                  this.groupedFaqs[ keyword.value ] = [];
                }

                //duplicate the faq item so all copies can be expanded individually
                const faqItemCopy:IFaqItem = _.clone(faqItem);
                faqItemCopy.scrollTarget = !index? 'faq-' + faqItem.id : undefined; // only the first one gets a scroll target
                this.groupedFaqs[ keyword.value ].push(faqItemCopy);
                this.faqs.push(faqItemCopy);
              });
            });


            this.keywords = _.sortBy(this.keywords, _.identity);
          }
        })
        .then(() => this.find())
        .finally(() => {
          this.setBusy(force, false);
        });
    }
  }
}
